import React, { useState, useEffect, useMemo, useRef } from 'react';
import Draggable from 'react-draggable';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Grid,
    CircularProgress,
    Button,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import AssetService from '../../../../Services/assets.service';
import {
    computeCapitalGains,
    computeTaxSummary,
    getCapitalGainTaxRatesForYear,
    computeSelectedEnhancementsTotals,
    buildReplacementSummary,
    hasReplaceableTaxableGain,
    getAvailableRealGainForReplacement,
    getAvailableInflationaryGainForReplacement,
    isReplacementInvalidAfterEnhancements,
    computeAdjustedGainsAfterReplacement,
    computeAdjustedGainBeforeTax,
    computeAfterReplacementCost,
    getBeforeReplacementCost,
    mapSavedReplacementsToReplacementData,
    mapSavedCapitalGainEnhancementToChild,
} from '../../../../Utils/capitalGainCalculations';
import CapitalGainReplacementModal from './CapitalGainReplacementModal.jsx';
import {
    getMaxCapitalGainTaxConfig,
    normalizeTaxRate,
} from '../../../../Utils/capitalGainTaxConfig';
import {
    BoundsWrapper,
    EMPTY_REPLACEMENT_DATA,
    isAssetSold,
    getAssetIdValue,
    sanitizeFileNamePart,
    formatEnhancementAssetLabel,
    getEnhancementAssetId,
    SectionCard,
    EnhancementsPickerDialog,
    CapitalGainAssetDetailsSection,
    CapitalGainIndexCalculationSection,
    CapitalGainTaxCalculationSection,
} from './CapitalGainModalComponents';

const CapitalGainCalculationModal = ({
    open = false,
    onClose = () => {},
    asset = null,
    caseId,
    clientName,
    caseType,
    direction,
    token,
}) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);
    const isRtl = direction === 'rtl';

    const [loading, setLoading] = useState(false);
    const [cpiError, setCpiError] = useState(null);
    const [basis, setBasis] = useState(null);
    const [cpi, setCpi] = useState(null);

    const [salePriceInput, setSalePriceInput] = useState('');
    const [saleExpenses, setSaleExpenses] = useState('0');
    const [inflationaryTaxRate, setInflationaryTaxRate] = useState('');
    const [realFrom2003TaxRate, setRealFrom2003TaxRate] = useState('');
    const [realUntil2002TaxRate, setRealUntil2002TaxRate] = useState('');
    const [advancePaid, setAdvancePaid] = useState('0');
    const [isDownloadingCapitalGainPdf, setIsDownloadingCapitalGainPdf] = useState(false);
    const [enhancementsOpen, setEnhancementsOpen] = useState(false);
    const [relatedEnhancementAssets, setRelatedEnhancementAssets] = useState([]);
    const [loadingEnhancementCandidates, setLoadingEnhancementCandidates] = useState(false);
    const [savingEnhancements, setSavingEnhancements] = useState(false);
    const [selectedEnhancementAssets, setSelectedEnhancementAssets] = useState([]);
    const [savedEnhancementIds, setSavedEnhancementIds] = useState(new Set());
    const [savedEnhancementAssets, setSavedEnhancementAssets] = useState([]);
    const [replacementOpen, setReplacementOpen] = useState(false);
    const [replacementData, setReplacementData] = useState(EMPTY_REPLACEMENT_DATA);
    const [soldAsset, setSoldAsset] = useState(null);
    const [soldAssetLoading, setSoldAssetLoading] = useState(false);
    const [soldAssetError, setSoldAssetError] = useState(null);

    const effectiveSoldAsset = soldAsset || asset;
    const effectiveAssetId = getAssetIdValue(effectiveSoldAsset);
    const isSold = isAssetSold(effectiveSoldAsset);

    const mapSavedEnhancementsToSelectedAssets = (saved = []) =>
        saved.map((row) => mapSavedCapitalGainEnhancementToChild(row));

    const applySavedEnhancementsState = (saved = []) => {
        const mapped = mapSavedEnhancementsToSelectedAssets(saved);
        const savedIds = new Set(mapped.map(getEnhancementAssetId).filter(Boolean));
        setSelectedEnhancementAssets(mapped);
        setSavedEnhancementAssets(mapped);
        setSavedEnhancementIds(savedIds);
    };

    const inputFieldSx = (width) => ({
        width,
        maxWidth: width,
        '& .MuiInputBase-root': {
            height: 28,
        },
        '& .MuiInputBase-input': {
            py: 0,
            px: 0.75,
            fontSize: 13,
            textAlign: direction === 'rtl' ? 'left' : 'right',
            direction: 'ltr',
        },
    });

    const tableInputSx = (width) => ({
        width,
        maxWidth: width,
        mx: 'auto',
        '& .MuiInputBase-root': {
            height: 26,
            backgroundColor: '#fff',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9e9e9e',
        },
        '& .MuiInputBase-input': {
            py: 0,
            px: 0.5,
            fontSize: 13,
            textAlign: 'center',
            direction: 'ltr',
        },
    });

    useEffect(() => {
        if (!open || !caseId || !token || !asset?.id) return;

        let cancelled = false;

        const fetchSoldAsset = async () => {
            try {
                setSoldAssetLoading(true);
                setSoldAssetError(null);
                setSoldAsset(null);

                const response = await AssetService.GetAssetById(caseId, asset.id, token);
                if (cancelled) return;

                const fetchedAsset = response?.data ?? null;
                if (!response?.success || !fetchedAsset) {
                    throw new Error(response?.error || 'Sold asset was not found');
                }

                setSoldAsset(fetchedAsset);
            } catch (error) {
                console.error('Failed to fetch sold asset for capital gain modal:', error);
                if (!cancelled) {
                    setSoldAssetError(error);
                    setSoldAsset(asset);
                }
            } finally {
                if (!cancelled) setSoldAssetLoading(false);
            }
        };

        fetchSoldAsset();

        return () => {
            cancelled = true;
        };
    }, [open, caseId, asset?.id, token]);

    useEffect(() => {
        if (!open || !effectiveAssetId || !caseId || !token || soldAssetLoading) return;
        if (!isSold) return;

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setCpiError(null);
            setBasis(null);
            setCpi(null);

            try {
                const basisResp = await AssetService.GetCapitalGainDepreciationAtSale(
                    caseId,
                    effectiveAssetId,
                    token
                );
                if (cancelled) return;
                if (!basisResp?.success) {
                    throw new Error(basisResp?.error || t('FailedToLoadCapitalGainData'));
                }
                const basisData = basisResp.data;
                setBasis(basisData);
                setRelatedEnhancementAssets([]);

                const reductionContext = {
                    saleDate:
                        basisData.sellDate ||
                        effectiveSoldAsset?.sellDate ||
                        effectiveSoldAsset?.SellDate,
                    parentDepreciationPercentage:
                        basisData.parentDepreciationPercentage ??
                        basisData.ParentDepreciationPercentage ??
                        effectiveSoldAsset?.depreciationPercentage ??
                        effectiveSoldAsset?.DepreciationPercentage ??
                        0,
                };
                try {
                    const replResp = await AssetService.GetCapitalGainReplacements(
                        caseId,
                        effectiveAssetId,
                        token
                    );
                    if (cancelled) return;
                    if (replResp?.success && Array.isArray(replResp.data) && replResp.data.length > 0) {
                        setReplacementData(
                            mapSavedReplacementsToReplacementData(replResp.data, reductionContext)
                        );
                    } else {
                        setReplacementData(EMPTY_REPLACEMENT_DATA);
                    }
                } catch {
                    if (!cancelled) setReplacementData(EMPTY_REPLACEMENT_DATA);
                }

                try {
                    const enhResp = await AssetService.GetCapitalGainEnhancements(
                        caseId,
                        effectiveAssetId,
                        token
                    );
                    if (cancelled) return;
                    if (enhResp?.success && Array.isArray(enhResp.data)) {
                        applySavedEnhancementsState(enhResp.data);
                    } else {
                        applySavedEnhancementsState([]);
                    }
                } catch (error) {
                    console.error('Failed to load saved capital gain enhancements:', error);
                    if (!cancelled) applySavedEnhancementsState([]);
                }

                setSalePriceInput(
                    basisData.sellAmount != null && basisData.sellAmount !== ''
                        ? String(basisData.sellAmount)
                        : effectiveSoldAsset?.sellAmount != null &&
                            effectiveSoldAsset?.sellAmount !== ''
                          ? String(effectiveSoldAsset.sellAmount ?? effectiveSoldAsset.SellAmount)
                          : ''
                );

                const purchaseDate =
                    basisData.purchaseDate ||
                    effectiveSoldAsset?.purchaseDate ||
                    effectiveSoldAsset?.PurchaseDate;
                const saleDate =
                    basisData.sellDate ||
                    effectiveSoldAsset?.sellDate ||
                    effectiveSoldAsset?.SellDate;
                try {
                    const cpiResp = await AssetService.GetCapitalGainIndexCalculation(
                        purchaseDate,
                        saleDate,
                        token
                    );
                    if (cancelled) return;
                    if (!cpiResp?.success) {
                        setCpiError(cpiResp?.error || t('FailedToLoadCpiData'));
                    } else {
                        setCpi(cpiResp.data);
                    }
                } catch (cpiErr) {
                    if (!cancelled) {
                        setCpiError(cpiErr?.message || t('FailedToLoadCpiData'));
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    toast.error(err?.message || t('FailedToLoadCapitalGainData'));
                    setCpiError(err?.message);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, effectiveAssetId, caseId, token, soldAssetLoading, isSold, soldAsset, t]);

    const saleDate =
        basis?.sellDate ||
        effectiveSoldAsset?.sellDate ||
        effectiveSoldAsset?.SellDate;
    const saleYear = saleDate ? new Date(saleDate).getFullYear() : null;

    const maxTaxConfig = useMemo(
        () => getMaxCapitalGainTaxConfig(saleYear, caseType),
        [saleYear, caseType]
    );

    const taxYear = saleYear ?? new Date().getFullYear();

    const taxRatesConfig = useMemo(
        () => getCapitalGainTaxRatesForYear(taxYear),
        [taxYear]
    );

    useEffect(() => {
        if (!open) return;
        setInflationaryTaxRate(String(taxRatesConfig.inflationaryTaxableRate));
        setRealFrom2003TaxRate(String(taxRatesConfig.realFrom2003Rate));
        setRealUntil2002TaxRate(String(taxRatesConfig.realUntil2002Rate));
    }, [open, taxRatesConfig]);

    useEffect(() => {
        if (!open) {
            setEnhancementsOpen(false);
            setSelectedEnhancementAssets([]);
            setSavedEnhancementIds(new Set());
            setSavedEnhancementAssets([]);
            setRelatedEnhancementAssets([]);
            setReplacementOpen(false);
            setReplacementData(EMPTY_REPLACEMENT_DATA);
            setSoldAsset(null);
            setSoldAssetError(null);
            setSoldAssetLoading(false);
        }
    }, [open, asset?.id]);

    const saleDateForEnhancements =
        basis?.sellDate ||
        effectiveSoldAsset?.sellDate ||
        effectiveSoldAsset?.SellDate;
    const parentDepreciationPercentage =
        basis?.parentDepreciationPercentage ?? basis?.ParentDepreciationPercentage ?? 0;

    const { enhancementsBalance, enhancementsAccumulatedDepreciation } = useMemo(
        () =>
            computeSelectedEnhancementsTotals({
                selectedChildren: selectedEnhancementAssets,
                saleDate: saleDateForEnhancements,
                parentDepreciationPercentage,
            }),
        [selectedEnhancementAssets, saleDateForEnhancements, parentDepreciationPercentage]
    );

    const selectedEnhancementIds = useMemo(
        () => new Set(selectedEnhancementAssets.map(getEnhancementAssetId).filter(Boolean)),
        [selectedEnhancementAssets]
    );

    const toggleEnhancementAsset = (child) => {
        const childId = getEnhancementAssetId(child);
        setSelectedEnhancementAssets((prev) => {
            const exists = prev.some((a) => getEnhancementAssetId(a) === childId);
            if (exists) {
                return prev.filter((a) => getEnhancementAssetId(a) !== childId);
            }
            return [...prev, child];
        });
    };

    const confirmRemoveSavedEnhancements = async (removedSavedAssets) => {
        const removedNames = removedSavedAssets
            .map((asset) => formatEnhancementAssetLabel(asset))
            .join(', ');

        const result = await Swal.fire({
            title: 'האם אתה בטוח?',
            text: `האם להסיר את יתרת ההשבחות שנבחרה? ${removedNames}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'כן, הסר ושמור',
            cancelButtonText: 'ביטול',
            reverseButtons: true,
        });

        if (result.isConfirmed) return true;

        setSelectedEnhancementAssets((prev) => {
            const existingIds = new Set(prev.map(getEnhancementAssetId).filter(Boolean));
            const restored = removedSavedAssets.filter((asset) => {
                const id = getEnhancementAssetId(asset);
                return id && !existingIds.has(id);
            });
            return [...prev, ...restored];
        });

        return false;
    };

    const handleRemoveEnhancementAsset = async (asset) => {
        const assetId = getEnhancementAssetId(asset);
        const wasSaved = assetId && savedEnhancementIds.has(assetId);

        if (wasSaved) {
            const result = await Swal.fire({
                title: 'האם אתה בטוח?',
                text: `האם להסיר את יתרת ההשבחות ${formatEnhancementAssetLabel(asset)}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'כן, הסר',
                cancelButtonText: 'ביטול',
                reverseButtons: true,
            });

            if (!result.isConfirmed) return;
        }

        const nextSelected = selectedEnhancementAssets.filter(
            (item) => getEnhancementAssetId(item) !== assetId
        );
        setSelectedEnhancementAssets(nextSelected);

        if (!wasSaved || !caseId || !effectiveAssetId || !token) return;

        try {
            const remainingIds = nextSelected.map(getEnhancementAssetId).filter(Boolean);
            const resp = await AssetService.SaveCapitalGainEnhancements(
                caseId,
                {
                    soldAssetId: effectiveAssetId,
                    enhancementAssetIds: remainingIds,
                },
                token
            );
            if (!resp?.success) {
                throw new Error(resp?.error || t('FailedToRemoveEnhancements'));
            }

            applySavedEnhancementsState(resp.data || []);
            toast.success(t('EnhancementsRemovedSuccessfully') || 'יתרת ההשבחות הוסרה בהצלחה');
        } catch (error) {
            console.error('Failed to remove saved enhancement:', error);
            toast.error(
                error?.message || t('FailedToRemoveEnhancements') || 'שגיאה בהסרת יתרת ההשבחות'
            );
            setSelectedEnhancementAssets((prev) => {
                const ids = new Set(prev.map(getEnhancementAssetId).filter(Boolean));
                if (ids.has(assetId)) return prev;
                return [...prev, asset];
            });
        }
    };

    const gains = useMemo(() => {
        if (!basis || !cpi) return null;
        return computeCapitalGains({
            salePrice: salePriceInput || basis.sellAmount || 0,
            originalPrice: basis.originalPrice,
            accumulatedDepreciation: basis.accumulatedDepreciationAtSale,
            enhancementsBalance,
            enhancementsAccumulatedDepreciation,
            saleExpenses,
            indexIncrease: cpi.indexIncrease,
            purchaseDate:
                basis.purchaseDate ||
                effectiveSoldAsset?.purchaseDate ||
                effectiveSoldAsset?.PurchaseDate,
            saleDate: saleDateForEnhancements,
        });
    }, [
        basis,
        cpi,
        salePriceInput,
        saleExpenses,
        enhancementsBalance,
        enhancementsAccumulatedDepreciation,
        effectiveSoldAsset?.purchaseDate,
        effectiveSoldAsset?.PurchaseDate,
        saleDateForEnhancements,
    ]);

    const replacementSummary = useMemo(
        () => buildReplacementSummary({ replacementData, gains }),
        [replacementData, gains]
    );

    const adjustedGains = useMemo(
        () => computeAdjustedGainsAfterReplacement(gains, replacementSummary),
        [gains, replacementSummary]
    );

    const canApplyReplacement = useMemo(() => hasReplaceableTaxableGain(gains), [gains]);

    const maxRealGain = useMemo(
        () => getAvailableRealGainForReplacement(gains),
        [gains]
    );

    const maxInflationaryGain = useMemo(
        () => getAvailableInflationaryGainForReplacement(gains),
        [gains]
    );

    const resetReplacementData = async () => {
        setReplacementData(EMPTY_REPLACEMENT_DATA);

        if (!caseId || !effectiveAssetId || !token) return;

        const taxYear = saleDateForEnhancements
            ? new Date(saleDateForEnhancements).getFullYear()
            : new Date().getFullYear();

        const resp = await AssetService.SaveCapitalGainReplacements(
            caseId,
            {
                soldAssetId: effectiveAssetId,
                taxYear,
                rows: [],
            },
            token
        );

        if (!resp?.success) {
            throw new Error(resp?.error || t('FailedToSaveCapitalGainReplacements'));
        }
    };

    const openReplacementPicker = () => {
        setReplacementOpen(true);
    };

    const handleOpenEnhancementsPicker = async () => {
        if (!caseId || !effectiveAssetId || !token) return;
        setLoadingEnhancementCandidates(true);
        try {
            const resp = await AssetService.GetEnhancementCandidates(caseId, effectiveAssetId, token);
            if (!resp?.success) {
                throw new Error(resp?.error || t('FailedToLoadCapitalGainData'));
            }
            setRelatedEnhancementAssets(resp.data || []);
            setEnhancementsOpen(true);
        } catch (err) {
            toast.error(err?.message || t('FailedToLoadCapitalGainData'));
        } finally {
            setLoadingEnhancementCandidates(false);
        }
    };

    const saveSelectedEnhancements = async () => {
        if (!caseId || !effectiveAssetId || !token) return;

        const currentSelectedIds = new Set(
            selectedEnhancementAssets.map(getEnhancementAssetId).filter(Boolean)
        );

        const removedSavedAssets = savedEnhancementAssets.filter((asset) => {
            const id = getEnhancementAssetId(asset);
            return id && savedEnhancementIds.has(id) && !currentSelectedIds.has(id);
        });

        if (removedSavedAssets.length > 0) {
            const confirmed = await confirmRemoveSavedEnhancements(removedSavedAssets);
            if (!confirmed) return;
        }

        const hasSavedReplacement =
            Number(replacementData?.realReplacementAmount || 0) > 0 ||
            Number(replacementData?.inflationaryReplacementAmount || 0) > 0 ||
            (replacementData?.selectedAssets || []).length > 0;

        if (hasSavedReplacement && gains && isReplacementInvalidAfterEnhancements(replacementData, gains)) {
            const result = await Swal.fire({
                title: 'שחלוף נכסים לא תקין',
                text: 'סכום השחלוף אינו תקין לאחר שינוי יתרת ההשבחות. יתרת ההשבחות הקטינה את הרווח החייב ולכן יש לעדכן או להסיר את השחלוף.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'אפס שחלוף ושמור',
                cancelButtonText: 'ביטול',
                reverseButtons: true,
            });

            if (!result.isConfirmed) {
                setSelectedEnhancementAssets([...savedEnhancementAssets]);
                return;
            }

            try {
                await resetReplacementData();
            } catch (error) {
                console.error('Failed to reset replacement after enhancements change:', error);
                toast.error(
                    error?.message ||
                        t('FailedToSaveCapitalGainReplacements') ||
                        'שגיאה באיפוס נתוני השחלוף'
                );
                setSelectedEnhancementAssets([...savedEnhancementAssets]);
                return;
            }
        }

        const enhancementAssetIds = selectedEnhancementAssets
            .map(getEnhancementAssetId)
            .filter(Boolean);

        setSavingEnhancements(true);
        try {
            const resp = await AssetService.SaveCapitalGainEnhancements(
                caseId,
                {
                    soldAssetId: effectiveAssetId,
                    enhancementAssetIds,
                },
                token
            );
            if (!resp?.success) {
                throw new Error(resp?.error || t('FailedToSaveEnhancements'));
            }

            applySavedEnhancementsState(resp.data || []);
            toast.success(t('EnhancementsSavedSuccessfully') || 'יתרת ההשבחות נשמרה בהצלחה');
        } catch (error) {
            console.error('Failed to save capital gain enhancements:', error);
            toast.error(
                error?.message || t('FailedToSaveEnhancements') || 'שגיאה בשמירת יתרת ההשבחות'
            );
        } finally {
            setSavingEnhancements(false);
        }
    };

    const handleReplacementApply = (data) => {
        setReplacementData({
            selectedAssets: data.selectedAssets || [],
            realReplacementAmount: Number(data.realReplacementAmount) || 0,
            inflationaryReplacementAmount: Number(data.inflationaryReplacementAmount) || 0,
        });
    };

    const tax = useMemo(() => {
        if (!gains) return null;
        return computeTaxSummary({
            totalCapitalGain: gains.totalCapitalGain,
            inflationaryTaxableGain: adjustedGains.adjustedInflationaryTaxableGain,
            realFrom2003: adjustedGains.adjustedRealFrom2003,
            realUntil2002: adjustedGains.adjustedRealUntil2002,
            inflationaryTaxRate,
            realFrom2003TaxRate,
            realUntil2002TaxRate,
            advancePaid,
        });
    }, [
        gains,
        adjustedGains,
        inflationaryTaxRate,
        realFrom2003TaxRate,
        realUntil2002TaxRate,
        advancePaid,
    ]);

    const handleInflationaryTaxRateChange = (e) => {
        setInflationaryTaxRate(String(normalizeTaxRate(e.target.value, maxTaxConfig.rate)));
    };

    const handleRealFrom2003TaxRateChange = (e) => {
        setRealFrom2003TaxRate(String(normalizeTaxRate(e.target.value, maxTaxConfig.rate)));
    };

    const handleRealUntil2002TaxRateChange = (e) => {
        setRealUntil2002TaxRate(String(normalizeTaxRate(e.target.value, maxTaxConfig.rate)));
    };

    const buildCapitalGainPdfPayload = () => ({
        caseId,
        clientName: clientName || '',
        soldAssetId: effectiveAssetId,
        salePrice: Number(salePriceInput || basis?.sellAmount || 0),
        saleExpenses: Number(saleExpenses || 0),
        enhancementsBalance,
        enhancementsAccumulatedDepreciation,
        asset: {
            assetNumber:
                effectiveSoldAsset?.assetNumber ?? effectiveSoldAsset?.AssetNumber ?? basis?.assetNumber ?? '',
            name: basis?.name ?? effectiveSoldAsset?.name ?? effectiveSoldAsset?.Name ?? '',
            purchaseDate:
                basis?.purchaseDate ??
                effectiveSoldAsset?.purchaseDate ??
                effectiveSoldAsset?.PurchaseDate ??
                null,
            activationDate:
                basis?.activationDate ??
                effectiveSoldAsset?.activationDate ??
                effectiveSoldAsset?.ActivationDate ??
                null,
            sellDate:
                basis?.sellDate ?? effectiveSoldAsset?.sellDate ?? effectiveSoldAsset?.SellDate ?? null,
        },
        basis: basis
            ? {
                  name: basis.name,
                  assetNumber: basis.assetNumber,
                  purchaseDate: basis.purchaseDate,
                  activationDate: basis.activationDate,
                  sellDate: basis.sellDate,
                  originalPrice: Number(basis.originalPrice || 0),
                  accumulatedDepreciationAtSale: Number(basis.accumulatedDepreciationAtSale || 0),
                  remainingOriginalCost: Number(
                      gains?.remainingOriginalCost ?? basis.remainingOriginalCost ?? 0
                  ),
                  sellAmount: Number(salePriceInput || basis.sellAmount || 0),
              }
            : null,
        gains: gains
            ? {
                  totalCapitalGain: Number(gains.totalCapitalGain || 0),
                  remainingOriginalCost: Number(gains.remainingOriginalCost || 0),
                  inflationaryTaxableGain: Number(gains.inflationaryTaxableGain || 0),
                  inflationaryExemptGain: Number(gains.inflationaryExemptGain || 0),
                  realFrom2003: Number(gains.realFrom2003 || 0),
                  realUntil2002: Number(gains.realUntil2002 || 0),
                  realLoss: Number(gains.realLoss || 0),
                  totalAccumulatedDepreciation: Number(gains.totalAccumulatedDepreciation || 0),
              }
            : null,
        indexCalculation: cpi
            ? {
                  purchaseKnownIndexPeriod:
                      cpi.purchaseKnownIndexPeriod ?? cpi.PurchaseKnownIndexPeriod ?? null,
                  saleKnownIndexPeriod: cpi.saleKnownIndexPeriod ?? cpi.SaleKnownIndexPeriod ?? null,
                  selectedBaseDesc: cpi.selectedBaseDesc ?? cpi.SelectedBaseDesc ?? null,
                  purchaseKnownIndex: Number(cpi.purchaseKnownIndex ?? cpi.PurchaseKnownIndex ?? 0),
                  saleKnownIndex: Number(cpi.saleKnownIndex ?? cpi.SaleKnownIndex ?? 0),
                  indexIncreasePercent: Number(cpi.indexIncreasePercent ?? 0),
              }
            : null,
        selectedEnhancementAssets: (selectedEnhancementAssets || []).map((child) => ({
            assetNumber: child?.assetNumber ?? child?.AssetNumber ?? '',
            name: child?.assetName ?? child?.AssetName ?? child?.name ?? child?.Name ?? '',
            purchaseDate: child?.purchaseDate ?? child?.PurchaseDate ?? null,
            originalPrice: Number(child?.originalPrice ?? child?.OriginalPrice ?? 0),
            accumulatedDepreciationAtSale: Number(
                child?.accumulatedDepreciationAtSale ?? child?.AccumulatedDepreciationAtSale ?? 0
            ),
            remainingOriginalCostAtSale: Number(
                child?.remainingOriginalCostAtSale ??
                    child?.RemainingOriginalCostAtSale ??
                    child?.remainingOriginalCost ??
                    child?.RemainingOriginalCost ??
                    0
            ),
        })),
        replacementData: {
            realReplacementAmount: Number(replacementData?.realReplacementAmount || 0),
            inflationaryReplacementAmount: Number(replacementData?.inflationaryReplacementAmount || 0),
            selectedAssets: (replacementData?.selectedAssets || []).map((row) => ({
                assetNumber: row?.assetNumber ?? '',
                assetName: row?.assetName ?? '',
                originalPrice: Number(row?.originalPrice ?? 0),
                realReplacementAmount: Number(row?.realReplacementAmount ?? 0),
                inflationaryReplacementAmount: Number(row?.inflationaryReplacementAmount ?? 0),
                beforeReplacementCost: getBeforeReplacementCost(row),
                afterReplacementCost: computeAfterReplacementCost(row),
            })),
        },
        replacementSummary: replacementSummary
            ? {
                  realReplacementAmount: Number(replacementSummary.realReplacementAmount || 0),
                  inflationaryReplacementAmount: Number(
                      replacementSummary.inflationaryReplacementAmount || 0
                  ),
                  replacementFrom2003: Number(replacementSummary.replacementFrom2003 || 0),
                  replacementUntil2002: Number(replacementSummary.replacementUntil2002 || 0),
              }
            : null,
        taxRates: {
            inflationaryTaxRate: Number(inflationaryTaxRate || 0),
            realFrom2003TaxRate: Number(realFrom2003TaxRate || 0),
            realUntil2002TaxRate: Number(realUntil2002TaxRate || 0),
        },
        advancePaid: Number(advancePaid || 0),
        adjustedGains: adjustedGains
            ? {
                  adjustedInflationaryTaxableGain: Number(
                      adjustedGains.adjustedInflationaryTaxableGain || 0
                  ),
                  adjustedRealFrom2003: Number(adjustedGains.adjustedRealFrom2003 || 0),
                  adjustedRealUntil2002: Number(adjustedGains.adjustedRealUntil2002 || 0),
              }
            : null,
        adjustedGainBeforeTax: gains && adjustedGains
            ? computeAdjustedGainBeforeTax(gains, adjustedGains)
            : 0,
        tax: tax
            ? {
                  totalTax: Number(tax.totalTax || 0),
                  taxOnInflationary: Number(tax.taxOnInflationary || 0),
                  taxOnRealFrom2003: Number(tax.taxOnRealFrom2003 || 0),
                  taxOnRealUntil2002: Number(tax.taxOnRealUntil2002 || 0),
                  balanceDue: Number(tax.balanceDue || 0),
              }
            : null,
        maxTaxSection: maxTaxConfig?.section ?? null,
        maxTaxRate: maxTaxConfig?.rate ?? null,
    });

    const handleDownloadCapitalGainPdf = async () => {
        if (!caseId || !effectiveAssetId) return;

        try {
            setIsDownloadingCapitalGainPdf(true);

            const payload = buildCapitalGainPdfPayload();
            const blob = await AssetService.DownloadCapitalGainPdf(
                caseId,
                effectiveAssetId,
                payload,
                token
            );

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const assetNumber =
                effectiveSoldAsset?.assetNumber || effectiveSoldAsset?.AssetNumber || '';
            const clientNamePart = sanitizeFileNamePart(clientName);
            const fileNameParts = ['טופס_רווח_הון', caseId, clientNamePart, assetNumber].filter(Boolean);
            link.download = `${fileNameParts.join('_')}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(t('CapitalGainPdfDownloadSuccess') || 'טופס רווח הון הורד בהצלחה');
        } catch (error) {
            console.error('Failed to download capital gain PDF:', error);
            toast.error(t('CapitalGainPdfDownloadError') || 'שגיאה בהורדת טופס רווח הון');
        } finally {
            setIsDownloadingCapitalGainPdf(false);
        }
    };

    if (!open || !asset) return null;

    const assetTitle = `${effectiveSoldAsset?.assetNumber ?? effectiveSoldAsset?.AssetNumber ?? ''} - ${
        basis?.name || effectiveSoldAsset?.name || effectiveSoldAsset?.Name || ''
    }`;

    return (
        <BoundsWrapper>
            <Draggable handle=".capital-gain-calc-handle" nodeRef={nodeRef}>
                <Box
                    ref={nodeRef}
                    dir={direction}
                    sx={{
                        width: { xs: '96vw', md: 'min(96vw, 1100px)' },
                        maxHeight: 'min(92vh, 900px)',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
                        border: '1px solid #304FFF',
                        overflowY: 'auto',
                        direction,
                    }}
                >
                    <Box
                        className="capital-gain-calc-handle"
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'move',
                            marginBottom: 1,
                            flexDirection: isRtl ? 'row' : 'row-reverse',
                            direction,
                        }}
                    >
                        <Tooltip title={t('Close')}>
                            <IconButton onClick={onClose}>
                                <CancelIcon style={{ color: '#304FFF' }} />
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ width: 40, flexShrink: 0 }} />

                        <Typography
                            sx={{
                                fontSize: 18,
                                fontWeight: 700,
                                color: '#304FFF',
                                textAlign: isRtl ? 'left' : 'right',
                                flex: 1,
                            }}
                        >
                            {t('CapitalGainFormTitle') || 'רווח הון'} — {assetTitle}
                        </Typography>
                    </Box>

                    {!soldAssetLoading && !loading && isSold && basis && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: isRtl ? 'flex-end' : 'flex-start',
                                mb: 1,
                                direction,
                            }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleDownloadCapitalGainPdf}
                                disabled={isDownloadingCapitalGainPdf || !gains || !cpi}
                                sx={{ textTransform: 'none' }}
                            >
                                {isDownloadingCapitalGainPdf
                                    ? t('DownloadingCapitalGainForm') || 'מוריד...'
                                    : t('DownloadCapitalGainForm') || 'הורד טופס רווח הון'}
                            </Button>
                        </Box>
                    )}

                    {(soldAssetLoading || (loading && isSold)) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!soldAssetLoading && !isSold && (
                        <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 14, color: '#666' }}>
                                {t('CapitalGainNotSoldAsset') ||
                                    'לא ניתן לחשב רווח הון לנכס שלא נמכר'}
                            </Typography>
                            {soldAssetError && (
                                <Typography sx={{ fontSize: 12, color: '#999', mt: 1 }}>
                                    {soldAssetError?.message}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {!soldAssetLoading && !loading && isSold && basis && (
                        <Grid container spacing={1.5} sx={{ direction }}>
                            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: isRtl ? 2 : 1 } }}>
                                <SectionCard title={t('AssetDetails') || 'פרטי נכס'} direction={direction}>
                                    <CapitalGainAssetDetailsSection
                                        direction={direction}
                                        t={t}
                                        basis={basis}
                                        gains={gains}
                                        enhancementsBalance={enhancementsBalance}
                                        enhancementsAccumulatedDepreciation={enhancementsAccumulatedDepreciation}
                                        selectedEnhancementAssets={selectedEnhancementAssets}
                                        onRemoveEnhancementAsset={handleRemoveEnhancementAsset}
                                        onOpenEnhancementsPicker={handleOpenEnhancementsPicker}
                                        loadingEnhancementCandidates={loadingEnhancementCandidates}
                                        saleExpenses={saleExpenses}
                                        onSaleExpensesChange={setSaleExpenses}
                                        salePriceInput={salePriceInput}
                                        onSalePriceInputChange={setSalePriceInput}
                                        inputFieldSx={inputFieldSx}
                                    />
                                </SectionCard>
                            </Grid>

                            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: isRtl ? 1 : 2 } }}>
                                <SectionCard title={t('IndexCalculation') || 'חישוב מדד'} direction={direction}>
                                    <CapitalGainIndexCalculationSection
                                        direction={direction}
                                        t={t}
                                        isRtl={isRtl}
                                        cpi={cpi}
                                        cpiError={cpiError}
                                        gains={gains}
                                        onOpenReplacementPicker={openReplacementPicker}
                                    />
                                </SectionCard>
                            </Grid>

                            {tax && gains && cpi && (
                                <Grid item xs={12} sx={{ order: 3 }}>
                                    <SectionCard
                                        title={t('CapitalGainFormTitle') || 'רווח הון'}
                                        direction={direction}
                                    >
                                        <CapitalGainTaxCalculationSection
                                            direction={direction}
                                            t={t}
                                            gains={gains}
                                            tax={tax}
                                            replacementSummary={replacementSummary}
                                            adjustedGains={adjustedGains}
                                            inflationaryTaxRate={inflationaryTaxRate}
                                            onInflationaryTaxRateChange={handleInflationaryTaxRateChange}
                                            realFrom2003TaxRate={realFrom2003TaxRate}
                                            onRealFrom2003TaxRateChange={handleRealFrom2003TaxRateChange}
                                            realUntil2002TaxRate={realUntil2002TaxRate}
                                            onRealUntil2002TaxRateChange={handleRealUntil2002TaxRateChange}
                                            advancePaid={advancePaid}
                                            setAdvancePaid={setAdvancePaid}
                                            tableInputSx={tableInputSx}
                                            isRtl={isRtl}
                                            maxTaxConfig={maxTaxConfig}
                                        />
                                    </SectionCard>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
            </Draggable>
            <EnhancementsPickerDialog
                open={enhancementsOpen}
                onClose={() => setEnhancementsOpen(false)}
                direction={direction}
                t={t}
                assets={relatedEnhancementAssets}
                selectedIds={selectedEnhancementIds}
                onToggle={toggleEnhancementAsset}
                onSave={saveSelectedEnhancements}
                savingEnhancements={savingEnhancements}
                saleDate={saleDateForEnhancements}
                parentDepreciationPercentage={parentDepreciationPercentage}
            />
            <CapitalGainReplacementModal
                open={replacementOpen}
                onClose={() => setReplacementOpen(false)}
                onApply={handleReplacementApply}
                direction={direction}
                caseId={caseId}
                token={token}
                excludedAssetId={effectiveAssetId}
                soldAssetId={effectiveAssetId}
                initialReplacementRows={replacementData.selectedAssets}
                canApplyReplacement={canApplyReplacement}
                maxRealGain={maxRealGain}
                maxInflationaryGain={maxInflationaryGain}
                gains={gains}
                saleDate={saleDateForEnhancements}
                parentDepreciationPercentage={parentDepreciationPercentage}
            />
        </BoundsWrapper>
    );
};

export default CapitalGainCalculationModal;
